import React, { memo, useMemo } from 'react'
import { useSelector } from 'react-redux'
import type { GridPaginationModel } from '@mui/x-data-grid-pro'
import { EmptyTableMessage } from 'src/components/payments-ops/EmptyTableMessage'
import { GridTable } from 'src/components/shared/grid/GridTable'
import { PAYMENTS_OPS_PAGE_SIZE_OPTIONS } from 'src/constants/payments-ops/constants'
import { buildTransactionColumns } from 'src/constants/payments-ops/transactionsGrid'
import { useInternalPermissions } from 'src/hooks/useInternalPermissions'
import type { MenuActionItem } from 'src/interfaces/ui'
import type { PaymentsOpsTransaction } from 'src/interfaces/transactionsReview'
import {
  getIncomingTransactions,
  getIncomingTransactionsLoading,
  getIncomingTransactionsTotal,
  getTransactionsReviewSubmitting,
} from 'src/selectors/transactionsReview'
import { usePartnerNameMap } from 'src/utils/hooks/paymentsOps/usePartnerNameMap'
import {
  canAssignTransactionInvoice,
  canInitiateTransactionRefund,
} from 'src/utils/paymentsOps/incomingTransactionActions'

interface Props {
  paginationModel: GridPaginationModel
  onPaginationModelChange(model: GridPaginationModel): void
  onViewDetails(transaction: PaymentsOpsTransaction): void
  onAssign(transaction: PaymentsOpsTransaction): void
  onRefund(transaction: PaymentsOpsTransaction): void
}

const getRowId = (row: PaymentsOpsTransaction): string => {
  return row.id
}

const renderEmpty = () => {
  return <EmptyTableMessage message="No transactions are missing an invoice." />
}

const IncomingTransactionsTableComponent = ({
  paginationModel,
  onPaginationModelChange,
  onViewDetails,
  onAssign,
  onRefund,
}: Props) => {
  const rows = useSelector(getIncomingTransactions)
  const total = useSelector(getIncomingTransactionsTotal)
  const isLoading = useSelector(getIncomingTransactionsLoading)
  const isSubmitting = useSelector(getTransactionsReviewSubmitting)
  const nameMap = usePartnerNameMap()
  const permissions = useInternalPermissions()
  const canAssign = canAssignTransactionInvoice(permissions)
  const canRefund = canInitiateTransactionRefund(permissions)

  const columns = useMemo(() => {
    const buildActions = (transaction: PaymentsOpsTransaction): MenuActionItem[] => {
      const actions: MenuActionItem[] = []

      if (canAssign) {
        actions.push({
          label: 'Assign to invoice…',
          disabled: isSubmitting,
          onClick: () => {
            onAssign(transaction)
          },
        })
      }

      if (canRefund) {
        actions.push({
          label: 'Initiate refund…',
          disabled: isSubmitting,
          onClick: () => {
            onRefund(transaction)
          },
        })
      }

      return actions
    }

    return buildTransactionColumns({
      variant: 'incoming',
      nameMap,
      onViewDetails,
      // Neither action permitted: the builder drops the actions column (spec §7).
      buildActions: canAssign || canRefund ? buildActions : undefined,
      isSubmitting,
    })
  }, [canAssign, canRefund, isSubmitting, nameMap, onAssign, onRefund, onViewDetails])

  return (
    <GridTable<PaymentsOpsTransaction>
      rows={rows}
      columns={columns}
      getRowId={getRowId}
      isLoading={isLoading}
      fillHeight
      paginationMode="server"
      rowCount={total}
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      pageSizeOptions={PAYMENTS_OPS_PAGE_SIZE_OPTIONS}
      sortingMode="server"
      EmptyElement={renderEmpty}
      tableDataTestId="incoming-transactions-table"
      rowDataTestIdPrefix="incoming-transactions"
    />
  )
}

export const IncomingTransactionsTable = memo(IncomingTransactionsTableComponent)
